import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  IconButton,
  Chip,
  Collapse,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import VisibilityIcon from '@material-ui/icons/Visibility';
import DescriptionIcon from '@material-ui/icons/Description';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import {
  TektonPipelineRun,
  PipelineRunStatus,
  getPipelineRunStatus,
  getStatusColor,
  formatDuration,
  formatTime,
  getTektonApiVersion,
} from '../types';
import { PipelineRunLogsDialog, PipelineRunForLogs } from './PipelineRunLogsDialog';

const useStyles = makeStyles(theme => ({
  filterBar: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  filterSelect: {
    minWidth: 150,
  },
  tableContainer: {
    maxHeight: 500,
  },
  statusChip: {
    fontWeight: 600,
    minWidth: 90,
  },
  expandRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  expandedContent: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  taskList: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    overflowX: 'auto',
    padding: theme.spacing(1, 0),
  },
  taskNode: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 120,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    position: 'relative',
  },
  taskNodeStatus: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
    flexShrink: 0,
  },
  taskNodeName: {
    fontSize: 12,
    maxWidth: 120,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  taskNodeDuration: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  taskNodeClickable: {
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
  },
  taskConnector: {
    width: 24,
    height: 2,
    backgroundColor: theme.palette.divider,
    flexShrink: 0,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
}));

// TaskRun 状态信息
interface TaskRunInfo {
  name: string;
  pipelineTaskName: string;
  status: 'Succeeded' | 'Failed' | 'Running' | 'Pending' | 'Cancelled';
  startTime?: string;
  completionTime?: string;
}

// 获取 TaskRun 状态
const getTaskRunStatus = (taskRun: any): TaskRunInfo['status'] => {
  const conditions = taskRun?.status?.conditions;
  if (!conditions || conditions.length === 0) return 'Pending';
  
  const succeededCondition = conditions.find((c: any) => c.type === 'Succeeded');
  if (!succeededCondition) return 'Pending';
  
  if (succeededCondition.status === 'True') return 'Succeeded';
  if (succeededCondition.status === 'False') {
    if (succeededCondition.reason === 'TaskRunCancelled') return 'Cancelled';
    return 'Failed';
  }
  if (succeededCondition.status === 'Unknown') {
    if (succeededCondition.reason === 'Running') return 'Running';
    return 'Pending';
  }
  return 'Pending';
};

// 展开详情组件
const ExpandedRunDetails: React.FC<{
  run: TektonPipelineRun;
  clusterName: string;
  isExpanded: boolean;
  onTaskClick?: (taskName: string) => void;
  // 缓存相关
  cachedTaskRuns?: TaskRunInfo[];
  onTaskRunsLoaded?: (runName: string, taskRuns: TaskRunInfo[]) => void;
}> = ({ run, clusterName, isExpanded, onTaskClick, cachedTaskRuns, onTaskRunsLoaded }) => {
  const classes = useStyles();
  const kubernetesApi = useApi(kubernetesApiRef);
  const [taskRuns, setTaskRuns] = useState<TaskRunInfo[]>(cachedTaskRuns || []);
  const [loading, setLoading] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 判断 PipelineRun 是否正在运行
  const isRunning = getPipelineRunStatus(run) === 'Running' || getPipelineRunStatus(run) === 'Pending';

  // 获取 TaskRun 详情
  const fetchTaskRuns = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      // 根据 PipelineRun 的 apiVersion 确定使用的 Tekton API 版本
      const tektonApiVersion = getTektonApiVersion(run.apiVersion);
      
      // 获取 PipelineRun 详情（包含 childReferences）
      const response = await kubernetesApi.proxy({
        clusterName,
        path: `/apis/${tektonApiVersion}/namespaces/${run.metadata.namespace}/pipelineruns/${run.metadata.name}`,
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch PipelineRun details');
      }
      
      const pipelineRunDetail = await response.json();
      await processTaskRuns(pipelineRunDetail, tektonApiVersion);
    } catch (error) {
      console.error('Failed to fetch TaskRuns:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [kubernetesApi, clusterName, run.metadata.namespace, run.metadata.name, run.apiVersion]);

  // 处理 TaskRuns（根据 apiVersion 确定使用哪种格式）
  // v1beta1: status.taskRuns（内嵌的 TaskRun 状态）
  // v1: status.childReferences（需要额外 API 调用）
  const processTaskRuns = async (pipelineRunDetail: any, apiVersion: string) => {
    const taskRunInfos: TaskRunInfo[] = [];
    const isV1Beta1 = apiVersion.includes('v1beta1');
    
    if (isV1Beta1) {
      // v1beta1 格式：status.taskRuns 是一个对象，key 是 TaskRun 名称
      const embeddedTaskRuns = pipelineRunDetail.status?.taskRuns || {};
      for (const [taskRunName, taskRunData] of Object.entries(embeddedTaskRuns)) {
        const data = taskRunData as any;
        taskRunInfos.push({
          name: taskRunName,
          pipelineTaskName: data.pipelineTaskName || taskRunName,
          status: getTaskRunStatus(data),
          startTime: data.status?.startTime,
          completionTime: data.status?.completionTime,
        });
      }
      
      // 按 startTime 排序
      taskRunInfos.sort((a, b) => {
        if (!a.startTime) return 1;
        if (!b.startTime) return -1;
        return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      });
    } else {
      // v1 格式：status.childReferences
      const childRefs = pipelineRunDetail.status?.childReferences || [];
      
      for (const ref of childRefs) {
        if (ref.kind === 'TaskRun') {
          try {
            const taskRunResponse = await kubernetesApi.proxy({
              clusterName,
              path: `/apis/tekton.dev/v1/namespaces/${run.metadata.namespace}/taskruns/${ref.name}`,
            });
            
            if (taskRunResponse.ok) {
              const taskRunData = await taskRunResponse.json();
              taskRunInfos.push({
                name: ref.name,
                pipelineTaskName: ref.pipelineTaskName,
                status: getTaskRunStatus(taskRunData),
                startTime: taskRunData.status?.startTime,
                completionTime: taskRunData.status?.completionTime,
              });
            }
          } catch {
            taskRunInfos.push({
              name: ref.name,
              pipelineTaskName: ref.pipelineTaskName,
              status: 'Pending',
            });
          }
        }
      }
    }
    
    setTaskRuns(taskRunInfos);
    
    // 只有已完成的才缓存
    if (!isRunning && onTaskRunsLoaded) {
      onTaskRunsLoaded(run.metadata.name, taskRunInfos);
    }
  };

  // 展开时获取 TaskRun 详情
  useEffect(() => {
    if (!isExpanded) return;
    
    // 如果有缓存且不是运行中，直接使用缓存
    if (cachedTaskRuns && !isRunning) {
      setTaskRuns(cachedTaskRuns);
      return;
    }
    
    // 首次加载
    fetchTaskRuns(true);
    
    // 如果正在运行，启动轮询
    if (isRunning) {
      pollIntervalRef.current = setInterval(() => {
        fetchTaskRuns(false);
      }, 2000);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isExpanded, isRunning, cachedTaskRuns, fetchTaskRuns]);

  return (
    <Box className={classes.expandedContent}>
      {/* Task 拓扑图 */}
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">
            加载中...
          </Typography>
        </Box>
      ) : taskRuns.length > 0 ? (
        <Box className={classes.taskList}>
          {taskRuns.map((task, index) => (
              <React.Fragment key={task.name}>
                {index > 0 && <Box className={classes.taskConnector} />}
                <Box 
                  className={`${classes.taskNode} ${onTaskClick ? classes.taskNodeClickable : ''}`}
                  onClick={(e) => {
                    if (onTaskClick) {
                      e.stopPropagation();
                      onTaskClick(task.pipelineTaskName);
                    }
                  }}
                  title={onTaskClick ? '点击查看日志' : undefined}
                >
                  <Box
                    className={classes.taskNodeStatus}
                    style={{ backgroundColor: getStatusColor(task.status) }}
                  >
                    {task.status === 'Succeeded' ? '✓' : 
                     task.status === 'Failed' ? '✗' : 
                     task.status === 'Running' ? '●' : 
                     task.status === 'Cancelled' ? '−' : '○'}
                  </Box>
                  <Typography 
                    variant="body2" 
                    className={classes.taskNodeName}
                    title={task.pipelineTaskName}
                  >
                    {task.pipelineTaskName}
                  </Typography>
                  <Typography variant="body2" className={classes.taskNodeDuration}>
                    {task.startTime && task.completionTime
                      ? formatDuration(task.startTime, task.completionTime)
                      : task.status === 'Running'
                      ? '...'
                      : '-'}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
      ) : null}
    </Box>
  );
};

// Props 接口
export interface PipelineRunsTableProps {
  /** PipelineRun 列表 */
  runs: TektonPipelineRun[];
  /** 集群名称 */
  clusterName: string;
  /** Pipeline 名称列表 */
  pipelineNames?: string[];
  /** 是否显示筛选器 */
  showFilters?: boolean;
  /** 是否显示分页 */
  showPagination?: boolean;
  /** 每页默认行数 */
  defaultRowsPerPage?: number;
  /** 重新运行回调 */
  onRerun?: (run: TektonPipelineRun) => void;
  /** 是否显示重新运行按钮 */
  showRerunButton?: boolean;
  /** 自定义操作列渲染 */
  renderActions?: (run: TektonPipelineRun) => React.ReactNode;
}

export const PipelineRunsTable: React.FC<PipelineRunsTableProps> = ({
  runs,
  clusterName,
  pipelineNames = [],
  showFilters = true,
  showPagination = true,
  defaultRowsPerPage = 10,
  onRerun,
  showRerunButton = true,
  renderActions,
}) => {
  const classes = useStyles();
  
  // 筛选状态
  const [pipelineFilter, setPipelineFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<PipelineRunStatus>('All');
  
  // 分页状态
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  
  // 展开状态
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  // 日志对话框状态
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState<TektonPipelineRun | null>(null);
  const [initialTaskName, setInitialTaskName] = useState<string | undefined>(undefined);

  // TaskRun 缓存：runName -> TaskRunInfo[]
  const [taskRunsCache, setTaskRunsCache] = useState<Map<string, TaskRunInfo[]>>(new Map());

  // 缓存 TaskRuns 数据
  const handleTaskRunsLoaded = (runName: string, taskRuns: TaskRunInfo[]) => {
    setTaskRunsCache(prev => {
      const next = new Map(prev);
      next.set(runName, taskRuns);
      return next;
    });
  };

  // 用于强制刷新持续时间显示的计时器（当前时间戳）
  const [currentTime, setCurrentTime] = useState(Date.now());

  // 每秒更新当前时间，用于实时显示持续时间
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []); // 空依赖数组，确保只创建一次定时器

  // 自动展开正在运行的 PipelineRun
  useEffect(() => {
    const runningRuns = runs.filter(run => {
      const status = getPipelineRunStatus(run);
      return status === 'Running' || status === 'Pending';
    });
    
    if (runningRuns.length > 0) {
      setExpandedRows(prev => {
        const next = new Set(prev);
        runningRuns.forEach(run => next.add(run.metadata.name));
        return next;
      });
    }
  }, [runs]);

  // 切换行展开
  const toggleRowExpand = (name: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  // 查看日志
  const viewLogs = (run: TektonPipelineRun, taskName?: string) => {
    setSelectedRun(run);
    setInitialTaskName(taskName);
    setLogsDialogOpen(true);
  };

  // 获取唯一的 Pipeline 名称列表
  const uniquePipelineNames = pipelineNames.length > 0 
    ? pipelineNames 
    : [...new Set(runs.map(r => r.spec?.pipelineRef?.name).filter(Boolean))] as string[];

  // 筛选后的数据
  const filteredRuns = runs
    .filter(run => {
      if (pipelineFilter !== 'All' && run.spec?.pipelineRef?.name !== pipelineFilter) {
        return false;
      }
      if (statusFilter !== 'All' && getPipelineRunStatus(run) !== statusFilter) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      const timeA = new Date(a.status?.startTime || a.metadata.creationTimestamp).getTime();
      const timeB = new Date(b.status?.startTime || b.metadata.creationTimestamp).getTime();
      return timeB - timeA;
    });

  // 分页后的数据
  const paginatedRuns = showPagination
    ? filteredRuns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : filteredRuns;

  // 去掉名称中的 hash 前缀（和可能的 userId）
  const removeHashPrefix = (name: string): string => {
    // 新格式: hashUserId-pipelineName-timestamp (5位hash紧跟userId)
    // 或: hash-pipelineName-timestamp (5位hash后跟-)
    // 先尝试匹配带 userId 的格式（5位hash + 数字userId）
    const withUserId = name.replace(/^[a-z0-9]{5}\d+-/, '');
    if (withUserId !== name) return withUserId;
    // 否则匹配不带 userId 的格式（5位hash + -）
    return name.replace(/^[a-z0-9]{5}-/, '');
  };

  // 获取触发人显示名称
  // 直接从 annotation 获取邮箱
  const getTriggeredBy = (run: TektonPipelineRun): string => {
    // 从 annotation 获取触发人（邮箱或 userRef）
    const triggeredBy = run.metadata?.annotations?.['backstage.io/triggered-by'];
    if (triggeredBy) {
      // 如果是邮箱，只显示 @ 前面的部分
      if (triggeredBy.includes('@')) {
        return triggeredBy.split('@')[0];
      }
      return triggeredBy;
    }
    
    return '-';
  };

  return (
    <Box>
      {/* 筛选器 */}
      {showFilters && (
        <Box className={classes.filterBar}>
          <FormControl variant="outlined" size="small" className={classes.filterSelect}>
            <InputLabel>Pipeline</InputLabel>
            <Select
              value={pipelineFilter}
              onChange={(e) => setPipelineFilter(e.target.value as string)}
              label="Pipeline"
            >
              <MenuItem value="All">All</MenuItem>
              {uniquePipelineNames.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterSelect}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as PipelineRunStatus)}
              label="Status"
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Succeeded">Succeeded</MenuItem>
              <MenuItem value="Failed">Failed</MenuItem>
              <MenuItem value="Running">Running</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* 表格 */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>NAME</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell>STARTED</TableCell>
              <TableCell>DURATION</TableCell>
              <TableCell>TRIGGERED BY</TableCell>
              <TableCell>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRuns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No Pipeline Runs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRuns.map((run) => {
                const status = getPipelineRunStatus(run);
                const isExpanded = expandedRows.has(run.metadata.name);
                return (
                  <React.Fragment key={run.metadata.name}>
                    <TableRow
                      className={classes.expandRow}
                      onClick={() => toggleRowExpand(run.metadata.name)}
                    >
                      <TableCell padding="checkbox">
                        <IconButton size="small">
                          {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{removeHashPrefix(run.metadata.name)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={status}
                          className={classes.statusChip}
                          style={{
                            backgroundColor: getStatusColor(status),
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {formatTime(run.status?.startTime || run.metadata.creationTimestamp)}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const runStatus = getPipelineRunStatus(run);
                          const isCompleted = ['Succeeded', 'Failed', 'Cancelled'].includes(runStatus);
                          // 对于已完成的状态，使用 completionTime 或 lastTransitionTime 作为结束时间
                          let endTime = run.status?.completionTime;
                          if (isCompleted && !endTime) {
                            // 尝试从 conditions 中获取 lastTransitionTime
                            const succeededCondition = run.status?.conditions?.find(c => c.type === 'Succeeded');
                            endTime = (succeededCondition as any)?.lastTransitionTime;
                          }
                          return formatDuration(
                            run.status?.startTime || run.metadata.creationTimestamp,
                            endTime,
                            isCompleted ? undefined : currentTime
                          );
                        })()}
                      </TableCell>
                      <TableCell>{getTriggeredBy(run)}</TableCell>
                      <TableCell>
                        {renderActions ? (
                          renderActions(run)
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              title="查看详情"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRowExpand(run.metadata.name);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="查看日志"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewLogs(run);
                              }}
                            >
                              <DescriptionIcon fontSize="small" />
                            </IconButton>
                            {showRerunButton && onRerun && (
                              <IconButton
                                size="small"
                                title="重新运行"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRerun(run);
                                }}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <ExpandedRunDetails
                            run={run}
                            clusterName={clusterName}
                            isExpanded={isExpanded}
                            onTaskClick={(taskName) => viewLogs(run, taskName)}
                            cachedTaskRuns={taskRunsCache.get(run.metadata.name)}
                            onTaskRunsLoaded={handleTaskRunsLoaded}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 分页 */}
      {showPagination && filteredRuns.length > 0 && (
        <TablePagination
          component="div"
          count={filteredRuns.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="每页行数:"
        />
      )}

      {/* 日志对话框 */}
      <PipelineRunLogsDialog
        open={logsDialogOpen}
        onClose={() => {
          setLogsDialogOpen(false);
          setSelectedRun(null);
          setInitialTaskName(undefined);
        }}
        run={selectedRun as PipelineRunForLogs | null}
        clusterName={clusterName}
        enablePolling={selectedRun ? ['Running', 'Pending'].includes(getPipelineRunStatus(selectedRun)) : false}
        initialTaskName={initialTaskName}
      />
    </Box>
  );
};

export default PipelineRunsTable;
